// src/pages/Register.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { register } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import { 
  Truck, User, Mail, Phone, Lock, MapPin, 
  Car, FileText, AlertCircle 
} from 'lucide-react';
import { VEHICLE_TYPES, DELIVERY_ZONES } from '../utils/constants';
import { validateRegisterForm } from '../utils/validators';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    delivery_zone: '',
    vehicle_type: '',
    license_plate: '',
    driver_license_number: '',
    emergency_contact: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const validation = validateRegisterForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.warning('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setLoading(true);
    try {
      // Retirer confirmPassword avant d'envoyer
      const { confirmPassword, ...registerData } = formData;
      
      await dispatch(register(registerData)).unwrap();
      
      toast.success('Inscription réussie ! Vérifiez votre email pour confirmer votre compte.');
      navigate('/login');
      
    } catch (error) {
      toast.error(error || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Devenir livreur WANETERE-DELIVERY
          </h1>
          <p className="text-gray-600">
            Créez votre compte livreur en quelques minutes
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations personnelles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary-600" />
                Informations personnelles
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom complet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="Jean Dupont"
                    disabled={loading}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="jean@example.com"
                    disabled={loading}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`input ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="+225 07 00 00 00 00"
                    disabled={loading}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Contact d'urgence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact d'urgence *
                  </label>
                  <input
                    type="tel"
                    name="emergency_contact"
                    value={formData.emergency_contact}
                    onChange={handleChange}
                    className={`input ${errors.emergency_contact ? 'border-red-500' : ''}`}
                    placeholder="+225 07 00 00 00 00"
                    disabled={loading}
                  />
                  {errors.emergency_contact && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.emergency_contact}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Sécurité */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary-600" />
                Sécurité
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`input ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirmation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`input ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Informations de livraison */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary-600" />
                Informations de livraison
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Zone de livraison */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zone de livraison *
                  </label>
                  <select
                    name="delivery_zone"
                    value={formData.delivery_zone}
                    onChange={handleChange}
                    className={`input ${errors.delivery_zone ? 'border-red-500' : ''}`}
                    disabled={loading}
                  >
                    <option value="">Sélectionner une zone</option>
                    {DELIVERY_ZONES.map(zone => (
                      <option key={zone} value={zone}>{zone}</option>
                    ))}
                  </select>
                  {errors.delivery_zone && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.delivery_zone}
                    </p>
                  )}
                </div>

                {/* Type de véhicule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de véhicule *
                  </label>
                  <select
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleChange}
                    className={`input ${errors.vehicle_type ? 'border-red-500' : ''}`}
                    disabled={loading}
                  >
                    <option value="">Sélectionner un type</option>
                    <option value={VEHICLE_TYPES.BIKE}>🚲 Vélo</option>
                    <option value={VEHICLE_TYPES.MOTORBIKE}>🏍️ Moto</option>
                    <option value={VEHICLE_TYPES.CAR}>🚗 Voiture</option>
                    <option value={VEHICLE_TYPES.VAN}>🚐 Camionnette</option>
                    <option value={VEHICLE_TYPES.TRUCK}>🚚 Camion</option>
                  </select>
                  {errors.vehicle_type && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.vehicle_type}
                    </p>
                  )}
                </div>

                {/* Plaque d'immatriculation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plaque d'immatriculation
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    className="input"
                    placeholder="AB-1234-CD"
                    disabled={loading}
                  />
                </div>

                {/* Numéro de permis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro de permis
                  </label>
                  <input
                    type="text"
                    name="driver_license_number"
                    value={formData.driver_license_number}
                    onChange={handleChange}
                    className={`input ${errors.driver_license_number ? 'border-red-500' : ''}`}
                    placeholder="123456789"
                    disabled={loading}
                  />
                  {errors.driver_license_number && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.driver_license_number}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                En vous inscrivant, vous acceptez nos{' '}
                <a href="#" className="text-primary-600 hover:underline font-medium">
                  Conditions d'utilisation
                </a>{' '}
                et notre{' '}
                <a href="#" className="text-primary-600 hover:underline font-medium">
                  Politique de confidentialité
                </a>
                .
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Inscription en cours...
                </span>
              ) : (
                'Créer mon compte livreur'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Vous avez déjà un compte ?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;